<script setup lang="ts">
import { computed, ref } from "vue";
import { KeyRound, LogIn, ShieldCheck, UserRound } from "lucide-vue-next";
import { api } from "../services/api";
import type { AdminIdentity } from "../types";

const props = defineProps<{ hasUsers: boolean }>();
const emit = defineEmits<{ authenticated: [user: AdminIdentity] }>();

const mode = ref<"account" | "key">(props.hasUsers ? "account" : "key");
const username = ref("");
const password = ref("");
const adminKey = ref("");
const loading = ref(false);
const error = ref("");
const canSubmit = computed(() => mode.value === "key"
  ? adminKey.value.trim().length >= 32
  : username.value.trim().length >= 3 && password.value.length >= 10);

async function submit(): Promise<void> {
  if (!canSubmit.value) return;
  loading.value = true;
  error.value = "";
  try {
    const user = mode.value === "key"
      ? await api.adminKeyLogin(adminKey.value.trim())
      : await api.adminAccountLogin(username.value.trim(), password.value);
    adminKey.value = "";
    password.value = "";
    emit("authenticated", user);
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "登录失败";
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <main class="admin-login-shell">
    <section class="admin-login-panel">
      <div class="admin-login-brand"><ShieldCheck :size="25" /><span>AI 管理台</span></div>
      <h1>管理员登录</h1>

      <div class="auth-mode-switch" role="group" aria-label="登录方式">
        <button type="button" :class="{ active: mode === 'account' }" @click="mode = 'account'">
          <UserRound :size="16" />账号
        </button>
        <button type="button" :class="{ active: mode === 'key' }" @click="mode = 'key'">
          <KeyRound :size="16" />管理员 Key
        </button>
      </div>

      <form class="form-stack" @submit.prevent="submit">
        <template v-if="mode === 'account'">
          <label><span>用户名</span><input v-model="username" autocomplete="username" autofocus /></label>
          <label><span>密码</span><input v-model="password" type="password" autocomplete="current-password" /></label>
        </template>
        <label v-else><span>管理员 Key</span><input v-model="adminKey" type="password" autocomplete="off" autofocus /></label>
        <div v-if="error" class="inline-error auth-error">{{ error }}</div>
        <button class="button primary auth-submit" type="submit" :disabled="loading || !canSubmit">
          <LogIn :size="17" />{{ loading ? "登录中" : "登录" }}
        </button>
      </form>
    </section>
  </main>
</template>
